import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import SEO from '@/components/SEO';
import { CONTACT_INFO, ADULT_PROGRAMS, YOUTH_PROGRAMS } from '@/lib/constants';
import { useConversionTracking } from '@/hooks/useMarketingTracking';
import TrackedBookingLink from '@/components/TrackedBookingLink';
import { ShieldCheck, ArrowRight, ChevronDown } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const FAQItem = ({ question, answer, index }: { question: string, answer: string, index: number }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <FadeInWhenVisible delay={index * 0.1}>
      <div className="bg-brand-dark border border-white/5 rounded-sm overflow-hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-6 md:px-8 py-5 md:py-6 flex items-center justify-between text-left group hover:bg-white/5 transition-colors"
        >
          <h3 className="text-lg md:text-xl font-display font-bold text-white pr-8 tracking-tighter">
            {index + 1}. {question}
          </h3>
          <ChevronDown 
            className={`w-5 h-5 text-brand-blue flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="px-6 md:px-8 pb-5 md:pb-6 pt-2">
                <p className="text-base md:text-lg text-white/70 leading-relaxed">
                  <strong className="text-white font-semibold">Answer:</strong> {answer}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FadeInWhenVisible>
  );
};

export default function Programs() {
  const { trackPhoneCall } = useConversionTracking();

  const membershipRates = [
    {
      id: 1,
      title: 'Unlimited',
      label: 'ULTIMATE',
      price: '$195',
      period: 'Monthly',
      description: 'Total access to all classes and facilities.',
    },
    {
      id: 2,
      title: 'Twice Weekly',
      label: 'CORE',
      price: '$185',
      period: 'Monthly',
      description: 'Two technical sessions per week.',
    },
    {
      id: 3,
      title: 'Once Weekly',
      label: 'BASIC',
      price: '$145',
      period: 'Monthly',
      description: 'One specialized session per week.',
    },
    {
      id: 4,
      title: 'Drop In',
      label: 'FLEXIBLE',
      price: '$45',
      period: 'Per Class',
      description: 'Single class access for visiting practitioners.',
    },
  ];

  return (
    <div className="bg-brand-black min-h-screen">
      <SEO
        title="Brazilian Jiu Jitsu Training Programs | Delta BJJ"
        description=" Delta BJJ is Culver City's premier Jiu-Jitsu school. We offer Adult Gi & No-Gi, Youth, and advanced programs for all levels. Join us and start your free trial!"
        url="https://www.deltabjj.com/brazilian-jiu-jitsu-training-programs"
      />

      {/* Hero Header */}
      <section className="relative pt-36 md:pt-48 pb-16 md:pb-20 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <FadeInWhenVisible>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-display font-bold text-white tracking-tighter leading-[1.05] mb-8 md:mb-12 px-2 sm:px-4">
              Delta BJJ: Premier{' '}
              <span className="blue-gradient-text inline-block">Jiu-Jitsu Training Programs</span>
            </h1>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Page Introduction Copy */}
      <section className="py-12 md:py-16 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <FadeInWhenVisible>
            <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed mb-6">
              At Delta BJJ, our Brazilian jiu-jitsu training programs focus on technical precision, building a sharper mind, and a stronger body. We provide <a href="https://www.deltabjj.com/instructors" className="text-brand-blue hover:underline font-semibold">high-level instruction</a> that delivers results on and off the mats.
            </p>
            <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed mb-6">
              As a premier Brazilian jiu-jitsu academy serving Culver City and the surrounding Los Angeles area, our facility provides a structured, safe, and high-energy environment.
            </p>
            <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed">
              Whether you are looking for the most technical jiu-jitsu in West Los Angeles or a welcoming space to begin your training, our curriculum is designed for consistency and progress at every level.
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Adult Programs */}
      <section className="py-20 md:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-8 tracking-tighter uppercase leading-none">
              Adult <span className="text-brand-blue inline-block">Programs</span>
            </h2>
          </FadeInWhenVisible>
          
          {/* Adult Programs Section Introduction */}
          <FadeInWhenVisible delay={0.1}>
            <div className="mb-12 max-w-4xl">
              <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed mb-4">
                <strong className="text-white font-semibold">Adult BJJ Training Programs: Fundamentals to Advanced</strong>
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4">
                The adult programs curriculum offers a structured path focused on maximum efficiency and refined control. A full schedule of both Gi and No-Gi classes builds functional fitness and elite self-defense skills.
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed">
                Personal goals ranging from competition to self-development are supported through professional instruction and detailed sessions.
              </p>
            </div>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {ADULT_PROGRAMS.map((program, i) => (
              <FadeInWhenVisible key={program.id} delay={i * 0.1}>
                <div className="group relative bg-brand-dark overflow-hidden rounded-sm border border-white/5 transition-all duration-500 hover:border-brand-blue/50 h-full flex flex-col">
                  <div className="relative aspect-[4/5] overflow-hidden flex-shrink-0">
                    <img
                      src={program.image}
                      alt={program.alt || program.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/20 to-transparent" />
                    
                    <div className="absolute bottom-10 left-10">
                      <span className="text-white font-display text-[10px] tracking-[0.3em] uppercase mb-4 block font-bold">{program.label}</span>
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-white tracking-tighter break-words">{program.title}</h3>
                    </div>
                  </div>
                  
                  <div className="p-8 flex-grow flex flex-col">
                    <p className="text-white font-bold tracking-widest uppercase text-sm mb-6">{program.description}</p>
                    <p className="text-white/50 text-base leading-relaxed mb-8 font-light italic flex-grow">"{program.details}"</p>
                    <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-auto">
                      <TrackedBookingLink 
                        href="/#book-trial" 
                        className="btn-premium-blue text-sm px-6 py-3 flex items-center gap-2 group-hover:scale-105 transition-transform"
                        target="_blank"
                      >
                        <span>Book Your Free Trial</span>
                        <ArrowRight className="w-4 h-4" />
                      </TrackedBookingLink>
                    </div>
                  </div>
                </div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* Youth Programs */}
      <section className="py-20 md:py-32 px-6 lg:px-12 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-8 tracking-tighter uppercase leading-none">
              Youth <span className="text-brand-blue inline-block">Programs</span>
            </h2>
          </FadeInWhenVisible>
          
          {/* Youth Programs Section Introduction */}
          <FadeInWhenVisible delay={0.1}>
            <div className="mb-12 max-w-4xl">
              <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed mb-4">
                <strong className="text-white font-semibold">Youth BJJ: Discipline and Practical Skill Development</strong>
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4">
                The youth training curriculum focuses on building foundational coordination, respect, and physical discipline. As a specialized jiu-jitsu academy, the program emphasizes practical self-defense and character development through structured Gi training.
              </p>
              <p className="text-base md:text-lg text-white/70 leading-relaxed">
                From the Little Champions to the Teen BJJ groups, classes are designed to foster confidence in a safe, high-energy environment.
              </p>
            </div>
          </FadeInWhenVisible>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {YOUTH_PROGRAMS.map((program, i) => (
              <FadeInWhenVisible key={program.id} delay={i * 0.1}>
                <div className="group relative bg-brand-dark overflow-hidden rounded-sm border border-white/5 transition-all duration-500 hover:border-brand-blue/50 h-full flex flex-col">
                  <div className="relative aspect-[4/5] overflow-hidden flex-shrink-0">
                    <img
                      src={program.image}
                      alt={program.alt || program.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/20 to-transparent" />
                    
                    <div className="absolute bottom-10 left-10">
                      <span className="text-white font-display text-[10px] tracking-[0.3em] uppercase mb-4 block font-bold">{program.label}</span>
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-white tracking-tighter">{program.title}</h3>
                    </div>
                  </div>
                  
                  <div className="p-8 flex-grow flex flex-col">
                    <p className="text-white font-bold tracking-widest uppercase text-sm mb-6">{program.description}</p>
                    <p className="text-white/50 text-base leading-relaxed mb-8 font-light italic flex-grow">"{program.details}"</p>
                    <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-auto">
                      <TrackedBookingLink 
                        href="/#book-trial" 
                        className="btn-premium-blue text-sm px-6 py-3 flex items-center gap-2 group-hover:scale-105 transition-transform"
                        target="_blank"
                      >
                        <span>Book Your Free Trial</span>
                        <ArrowRight className="w-4 h-4" />
                      </TrackedBookingLink>
                    </div>
                  </div>
                </div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Section - High-End Price Table */}
      <section className="py-12 md:py-20 px-6 lg:px-12 bg-gradient-to-b from-white via-gray-50/50 to-white relative">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-8 md:mb-12 gap-6">
            <FadeInWhenVisible>
              <span className="text-brand-blue font-display text-xs tracking-[0.4em] uppercase mb-4 block font-bold">Investment</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-bold tracking-tighter leading-tight px-4">
                CHOOSE YOUR <br />
                <span className="text-gray-500">COMMITMENT.</span>
              </h2>
            </FadeInWhenVisible>
            <FadeInWhenVisible delay={0.2}>
              <p className="text-gray-500 max-w-sm text-sm font-light tracking-wide uppercase">
                Premium access for those who demand excellence in their training and environment.
              </p>
            </FadeInWhenVisible>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {membershipRates.map((membership, i) => (
              <FadeInWhenVisible key={membership.id} delay={i * 0.1}>
                <div className="bg-white border-2 border-gray-200 rounded-lg shadow-sm hover:shadow-xl hover:border-brand-blue/50 transition-all duration-300 group h-full flex flex-col relative overflow-hidden">
                  {/* Decorative accent bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-blue via-brand-blue/80 to-brand-blue/60"></div>
                  
                  <div className="p-6 md:p-8 flex flex-col flex-grow">
                    <span className="text-brand-blue font-display text-[10px] tracking-[0.3em] uppercase mb-4 block font-bold">{membership.label}</span>
                    <h3 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tighter leading-tight">{membership.title}</h3>
                    <div className="mb-6 flex items-baseline">
                      <span className="text-4xl md:text-5xl font-display font-bold leading-none">{membership.price}</span>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-2 whitespace-nowrap">{membership.period}</span>
                    </div>
                    <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow" style={{ 
                      textWrap: 'pretty',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word',
                      hyphens: 'auto'
                    }}>
                      {membership.description.split(' ').map((word, idx, arr) => {
                        // Prevent widows: keep last 2 words together
                        if (idx >= arr.length - 2 && arr.length > 2) {
                          return idx === arr.length - 2 ? word : `\u00A0${word}`;
                        }
                        return idx === 0 ? word : ` ${word}`;
                      })}
                    </p>
                    <TrackedBookingLink href="/#book-trial" className="btn-premium-blue w-full text-center mt-auto">
                      Join the Family
                    </TrackedBookingLink>
                  </div>
                </div>
              </FadeInWhenVisible>
            ))}
          </div>
        </div>
      </section>

      {/* CTA section - Moved above FAQ */}
      <section className="py-16 md:py-24 px-6 lg:px-12 bg-gradient-to-br from-brand-blue/10 via-white to-brand-blue/5 relative overflow-hidden border-t border-gray-200">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(29,76,162,0.1),transparent_50%)] pointer-events-none"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <FadeInWhenVisible>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-brand-black mb-6 leading-none tracking-tighter">
              NOT SURE WHERE <br />
              <span className="text-brand-blue">TO START?</span>
            </h2>
            <p className="text-gray-700 text-base md:text-lg mb-10 max-w-2xl mx-auto font-medium">
              Schedule a free consultation and two introductory classes.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <TrackedBookingLink href="/#book-trial" className="btn-premium-blue text-base px-8 py-4 font-extrabold">
                Book 2 Free Trials
              </TrackedBookingLink>
              <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} onClick={trackPhoneCall} className="btn-premium-outline-dark text-base px-8 py-4 font-extrabold flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-brand-blue" />
                <span>Call Us Today</span>
              </a>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 px-6 lg:px-12 bg-zinc-950 border-t-4 border-brand-blue/20">
        <div className="max-w-4xl mx-auto">
          <FadeInWhenVisible>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-12 tracking-tighter text-center">
              New to Jiu-Jitsu? We've Got You Covered with these <span className="blue-gradient-text">frequently asked questions.</span>
            </h2>
          </FadeInWhenVisible>
          
          <div className="space-y-4">
            {[
              {
                question: "Do I need to be in shape to start Jiu-Jitsu?",
                answer: "Not at all! Most people use BJJ as a tool to get in shape. Our training programs are designed to help you build functional strength, flexibility, and cardiovascular endurance at your own pace. Whether your goal is weight loss or functional fitness, you'll find a supportive environment here to help you reach it."
              },
              {
                question: "What should I wear and bring to my first class?",
                answer: "For your first class, wear a comfortable t-shirt or rashguard and athletic shorts (without zippers or pockets). If you are attending a Gi class, we provide loaner uniforms for our trial students. Don't forget a water bottle and a pair of sandals or flip-flops to wear when you are off the mats."
              },
              {
                question: "What is the difference between Gi and No-Gi BJJ?",
                answer: "Gi training involves a traditional uniform, focusing on technical grips and leverage using the fabric. No-Gi is practiced in a rashguard and shorts, favoring a faster, more fluid style. At Delta BJJ, our curriculum covers both styles to ensure well-rounded technical mastery."
              },
              {
                question: "Is Brazilian Jiu-Jitsu safe for my child?",
                answer: "Safety is our top priority. Our Youth BJJ programs in Culver City are conducted in a highly supervised environment. We focus on \"controlled rolling\" and technical drills that build confidence and coordination without the impact found in other contact sports."
              },
              {
                question: "How often should a beginner train?",
                answer: "For most beginners, we recommend starting with 2–3 classes per week. This allows your body to adjust to the movements while ensuring consistent progress through our BJJ fundamentals curriculum. Our flexible morning, midday, and evening schedule makes it easy to find sessions that fit your lifestyle."
              }
            ].map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer Separator - Clear visual break */}
      <div className="h-1 bg-gradient-to-r from-transparent via-brand-blue/30 to-transparent"></div>
    </div>
  );
}
